import { useCallback, useEffect, useState } from "react";

/**
 * Выбор человека: светлая, тёмная или «как в системе».
 *
 * Системная — отдельное значение, а не вычисленное однажды: она обязана
 * следовать за системой и после того, как вкладка открыта. Свести её к
 * «светлой» в момент первого рендера значило бы запомнить ответ на вопрос,
 * который задаётся каждый раз заново.
 */
export type ThemeChoice = "light" | "dark" | "system";

/** Во что выбор разворачивается на самом деле — третьего у темы нет. */
export type ResolvedTheme = "light" | "dark";

/**
 * Ключ в `localStorage`. Своё имя, а не общее `theme`: на том же домене
 * рядом живут собранная статика и dev-сервер, и общий ключ разошёлся бы
 * смыслом между ними.
 */
const STORAGE_KEY = "screener.theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

function isChoice(value: string | null): value is ThemeChoice {
  return value === "light" || value === "dark" || value === "system";
}

/**
 * Сохранённый выбор. Чтение `localStorage` обёрнуто: в приватном окне и при
 * запрещённых данных сайта обращение бросает, и упавшее хранилище не имеет
 * права уронить весь интерфейс — тема тогда просто системная.
 */
export function readStoredChoice(storage: Pick<Storage, "getItem"> | null): ThemeChoice {
  try {
    const stored = storage?.getItem(STORAGE_KEY) ?? null;
    return isChoice(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

/** Во что разворачивается выбор при данном состоянии системной темы. */
export function resolveTheme(choice: ThemeChoice, systemPrefersDark: boolean): ResolvedTheme {
  if (choice === "system") {
    return systemPrefersDark ? "dark" : "light";
  }
  return choice;
}

/** Следующий выбор по нажатию: светлая → тёмная → как в системе → светлая. */
export function nextChoice(choice: ThemeChoice): ThemeChoice {
  if (choice === "light") {
    return "dark";
  }
  return choice === "dark" ? "system" : "light";
}

/**
 * Тема интерфейса. Класс `dark` вешается на корень документа — тот же
 * вариант, которым заданы токены в `globals.css`, — и переживает
 * перезагрузку через `localStorage`.
 *
 * Компонентам тема не передаётся: цвет берётся токенами, и знать о выборе
 * обязан только сам переключатель.
 */
export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(() => {
    // Сам геттер `window.localStorage` бросает при запрещённых данных сайта,
    // до `getItem` и его защиты в `readStoredChoice`.
    try {
      return readStoredChoice(typeof window === "undefined" ? null : window.localStorage);
    } catch {
      return readStoredChoice(null);
    }
  });
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => typeof window !== "undefined" && window.matchMedia(DARK_QUERY).matches,
  );

  // Системная тема меняется и при открытой вкладке: расписание «тёмная
  // с заката» переключает её под руками, и выбор «как в системе» обязан
  // за этим пойти.
  useEffect(() => {
    const media = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches);
    media.addEventListener("change", onChange);
    setSystemPrefersDark(media.matches);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const resolved = resolveTheme(choice, systemPrefersDark);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", resolved === "dark");
  }, [resolved]);

  const choose = useCallback((next: ThemeChoice) => {
    setChoice(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Запрещённое хранилище — не отказ переключения: тема сменится, просто
      // не переживёт перезагрузку.
    }
  }, []);

  // Через `choose`, а не обновлением состояния функцией: запись в хранилище —
  // побочное действие, а функцию обновления React вправе вызвать дважды.
  const toggle = useCallback(() => {
    choose(nextChoice(choice));
  }, [choice, choose]);

  return { choice, resolved, choose, toggle };
}
